import {
  AbortContext,
  GROUP_TYPE,
  IForwardToFilters,
  IForwardToLoadingStates,
  IMessageData,
  IRecentContacts,
  IUserDirectory,
  RECENT_CONTACTS_BUCKET,
  USER_TYPE,
} from "@home-health-notify/domain";
import {
  Constants,
  debounce,
  MessagePanelContext,
  OrganizationContext,
  useDebouncedAbortableCall,
  useScrollLock,
  useTranslation,
} from "hhn-commons";
import { CircularProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import CommonDialog from "../../../common/CommonDialog/CommonDialog";
import { SendButton } from "../MessagePanel.style";
import {
  getBucketForTab,
  searchPrivateContacts,
  setInitialFilters,
  TAB,
} from "./ForwardToHelper";
import ForwardToPanel from "./ForwardToPanel";
import { recordUnconfirmedForward } from "../../../utils/unconfirmedForwardNotice";

const EMPTY_SEARCH_BY_TAB: Record<TAB, string> = {
  [TAB.PATIENT]: "",
  [TAB.INTERNAL]: "",
  [TAB.PRIVATE]: "",
};

interface ForwardToProps {
  closeModal: () => void;
  isModalOpen: boolean;
  selectedMessagesToForward: Partial<IMessageData>[];
  closeForwardMessagesActionPanel: () => void;
}

function ForwardTo({
  closeModal,
  isModalOpen,
  selectedMessagesToForward,
  closeForwardMessagesActionPanel,
}: ForwardToProps) {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "chat" });

  const { enqueueSnackbar } = useSnackbar();
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { clearSelectedMessages } = useContext(MessagePanelContext);
  const [isForwarding, setIsForwarding] = useState(false);
  const selectedRole = selectedOrgUser?.getOrganization().userProfile.type;
  const [selectedContactsToForwardTo, setSelectedContactsToForwardTo] =
    useState<IRecentContacts[]>([]);
  const [forwardToList, setForwardToList] = useState<IRecentContacts[]>([]);
  const [isLoading, setIsLoading] = useState<IForwardToLoadingStates>({
    isTabListLoading: false,
    isSearchLoading: false,
    isNextPageLoading: false,
  });
  const [filters, setFilters] = useState<IForwardToFilters>({
    search: "",
    groupType: GROUP_TYPE.PATIENT,
    userType: undefined,
  });
  const [selectedTabValue, setSelectedTabValue] = React.useState<number>(
    TAB.PATIENT,
  );
  const [searchByTab, setSearchByTab] =
    useState<Record<TAB, string>>(EMPTY_SEARCH_BY_TAB);
  const searchAbortContext = React.useRef<AbortContext>(undefined);
  const { concurrencySafe } = useScrollLock();
  const isPrivateTab = selectedTabValue === TAB.PRIVATE;
  const activeBucket = isPrivateTab
    ? undefined
    : getBucketForTab(selectedTabValue as TAB.PATIENT | TAB.INTERNAL);

  async function fetchRecentContacts(
    bucket: RECENT_CONTACTS_BUCKET,
    filters: IForwardToFilters,
    context: AbortContext,
    paginate?: boolean,
  ) {
    const runner = async () => {
      setIsLoading({
        isTabListLoading: !!(filters.groupType || filters.userType),
        isSearchLoading: (filters.search ?? "").length > 0,
        isNextPageLoading: paginate ?? false,
      });
      if (selectedOrgUser) {
        await selectedOrgUser.fetchRecentContacts(
          bucket,
          filters,
          context,
          paginate,
        );
        setForwardToList(selectedOrgUser.getRecentContacts(bucket).data);
      }
      if (!context.isAborted) {
        setIsLoading({
          isTabListLoading: false,
          isSearchLoading: false,
          isNextPageLoading: false,
        });
      }
    };
    if (paginate) {
      await concurrencySafe(runner);
    } else {
      await runner();
    }
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTabValue(newValue);
  };

  const finishForwarding = () => {
    clearSelectedMessages?.();
    closeModal();
    closeForwardMessagesActionPanel();
  };

  const forwardMessage = async () => {
    if (!selectedOrgUser) {
      return;
    }

    // The dialog stays open until the server answers. Closing it on click, as
    // it used to, meant a forward that never landed left no trace at all — the
    // selection was cleared and the user had no reason to doubt it worked.
    setIsForwarding(true);
    selectedOrgUser.forwardMessage(
      selectedContactsToForwardTo,
      selectedMessagesToForward,
      data => {
        setIsForwarding(false);
        if (data.statusCode === 200) {
          enqueueSnackbar(tNs("forwardTo.forwardSuccess"), {
            autoHideDuration: 3000,
          });
          finishForwarding();
          return;
        }
        // Left open, with the selection intact, so the forward can be retried
        // without picking everyone again.
        enqueueSnackbar(tNs("forwardTo.forwardError"), {
          autoHideDuration: 3000,
        });
      },
      () => {
        setIsForwarding(false);
        // Losing the connection also raises the offline overlay, which reloads
        // the page the moment the network returns — so the note has to outlive
        // this toast to be read at all.
        recordUnconfirmedForward(selectedContactsToForwardTo.length);
        enqueueSnackbar(tNs("forwardTo.forwardUnconfirmed"), {
          variant: "warning",
          autoHideDuration: 6000,
        });
      },
    );
  };

  const resetTabToIdle = (bucket?: RECENT_CONTACTS_BUCKET) => {
    if (bucket && selectedOrgUser) {
      selectedOrgUser.resetRecentContactsData(bucket);
    }
    setForwardToList([]);
    setIsLoading({
      isTabListLoading: false,
      isSearchLoading: false,
      isNextPageLoading: false,
    });
  };

  // Private has no domain bucket: it searches the already-loaded user
  // directory locally instead of fetching. Still debounced (like the other
  // two tabs' network search) so filtering/sorting the directory doesn't run
  // synchronously inside the keystroke handler — otherwise it blocks the
  // input's own re-render until it's done, making typing feel laggy.
  const debouncedSearchPrivateTab = React.useRef(
    debounce(
      (
        userDirectory: IUserDirectory,
        role: USER_TYPE | undefined,
        searchText: string,
      ) => {
        // The empty check lives inside the debounce (not before calling it),
        // same as handleSearch below — so backspacing to empty always
        // supersedes/cancels whatever search was still pending.
        if (!searchText.trim()) {
          setForwardToList([]);
          return;
        }
        setForwardToList(
          searchPrivateContacts(userDirectory, role, searchText),
        );
      },
      Constants.API_DEBOUNCE_DELAY_IN_MS,
    ),
  ).current;

  const searchPrivateTab = (searchText: string) => {
    if (!selectedOrgUser) return;
    debouncedSearchPrivateTab(
      selectedOrgUser.userDirectory,
      selectedRole,
      searchText,
    );
  };

  const handleSearch = useDebouncedAbortableCall(
    async (
      abortContext: AbortContext,
      updatedFilters: IForwardToFilters,
      bucket: RECENT_CONTACTS_BUCKET,
    ) => {
      searchAbortContext.current?.abort();
      searchAbortContext.current = abortContext;
      if (!(updatedFilters.search ?? "").trim()) {
        resetTabToIdle(bucket);
        return;
      }
      await fetchRecentContacts(bucket, updatedFilters, abortContext);
    },
    Constants.API_DEBOUNCE_DELAY_IN_MS,
  );

  const onSearchTextChange = (text: string) => {
    const updatedFilters: IForwardToFilters = {
      ...filters,
      search: text,
    };
    setFilters(updatedFilters);
    setSearchByTab(prev => ({ ...prev, [selectedTabValue]: text }));
    if (isPrivateTab) {
      searchPrivateTab(text);
      return;
    }
    handleSearch(updatedFilters, activeBucket as RECENT_CONTACTS_BUCKET);
  };

  const onSearchClear = () => {
    searchAbortContext.current?.abort();
    searchAbortContext.current = new AbortContext();
    const updatedFilters: IForwardToFilters = { ...filters, search: "" };
    setFilters(updatedFilters);
    setSearchByTab(prev => ({ ...prev, [selectedTabValue]: "" }));
    resetTabToIdle(activeBucket);
  };

  useEffect(() => {
    if (!isModalOpen) {
      searchAbortContext.current?.abort(); // Cancel ongoing request on modal close
    }
  }, [isModalOpen]);

  useEffect(() => {
    return () => {
      searchAbortContext.current?.abort(); // cancel any ongoing request
    };
  }, []);

  useEffect(() => {
    // Every fresh open starts from a clean slate across all 3 tabs.
    if (!isModalOpen) return;

    searchAbortContext.current?.abort();
    searchAbortContext.current = new AbortContext();
    if (selectedOrgUser) {
      selectedOrgUser.resetRecentContactsData();
    }
    setIsForwarding(false);
    setSearchByTab(EMPTY_SEARCH_BY_TAB);
    setSelectedTabValue(TAB.PATIENT);
    setFilters(setInitialFilters(TAB.PATIENT, selectedRole));
    setForwardToList([]);
    setIsLoading({
      isTabListLoading: false,
      isSearchLoading: false,
      isNextPageLoading: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  useEffect(() => {
    // Switching tabs never re-fetches/re-searches: Patient/Internal read
    // their cached domain bucket; Private recomputes its local directory
    // search from the persisted search text (cheap enough to just redo).
    if (!isModalOpen) return;

    const persistedSearch = searchByTab[selectedTabValue as TAB];
    setFilters({
      ...setInitialFilters(selectedTabValue, selectedRole),
      search: persistedSearch,
    });
    if (isPrivateTab) {
      setForwardToList(
        persistedSearch.trim() && selectedOrgUser
          ? searchPrivateContacts(
              selectedOrgUser.userDirectory,
              selectedRole,
              persistedSearch,
            )
          : [],
      );
    } else {
      setForwardToList(
        (activeBucket &&
          selectedOrgUser?.getRecentContacts(activeBucket).data) ??
          [],
      );
    }
    setIsLoading({
      isTabListLoading: false,
      isSearchLoading: false,
      isNextPageLoading: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTabValue]);

  return (
    <CommonDialog
      isDialogOpen={isModalOpen}
      onDialogClose={closeModal}
      title={tNs("forwardTo.forwardToHeader")}
      titleAlignment="center"
      fillContentHeight
      actionContent={
        <SendButton
          variant="contained"
          disabled={selectedContactsToForwardTo.length === 0 || isForwarding}
          onClick={forwardMessage}
          startIcon={
            isForwarding ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {tNs(isForwarding ? "forwardTo.forwarding" : "forwardTo.forwardBtn")}
        </SendButton>
      }
    >
      <ForwardToPanel
        forwardToList={forwardToList}
        isLoading={isLoading}
        selectedContactsToForwardTo={selectedContactsToForwardTo}
        setSelectedContactsToForwardTo={setSelectedContactsToForwardTo}
        fetchRecentContacts={(
          filters?: IForwardToFilters,
          paginate?: boolean,
        ) => {
          // Private has no pagination: the local directory search always
          // returns its full result set synchronously.
          if (!activeBucket) return Promise.resolve();
          return fetchRecentContacts(
            activeBucket,
            filters ?? {
              search: "",
              groupType: GROUP_TYPE.PATIENT,
              userType: undefined,
            },
            searchAbortContext.current ?? new AbortContext(),
            paginate,
          );
        }}
        onSearchTextChange={onSearchTextChange}
        onSearchClear={onSearchClear}
        filters={filters}
        tabValue={selectedTabValue}
        handleTabChange={handleTabChange}
      />
    </CommonDialog>
  );
}

export default ForwardTo;
